import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { unreadCountQueryKey } from '@/features/alerts/useAlerts';

/**
 * When the service worker signals that a push arrived (task item 6), re-read the
 * alerts feed and the unread badge NOW rather than waiting on the 60 s poll. The
 * worker `postMessage`s `{ type: 'raad-push' }` from its `push` handler; we
 * invalidate here so the change is a targeted refetch, not a faster poll.
 *
 * Deliberately invalidates the whole `['alerts', ...]` prefix (every feed query,
 * whatever its range/vehicle/filter) AND the unread count — the same pair
 * mark-read invalidates, for the same reason: a badge that disagrees with the list
 * it links to is worse than no badge.
 */
export function usePushMessages(): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    // Capture the container so cleanup removes the listener from the SAME object we
    // added it to, even if `navigator.serviceWorker` is later swapped out.
    const sw = navigator.serviceWorker;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string } | null;
      if (!data || data.type !== 'raad-push') return;
      void qc.invalidateQueries({ queryKey: unreadCountQueryKey });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, [qc]);
}
